import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  IconButton,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Chip,
  Divider
} from '@mui/material';
import {
  Save,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { appConfig } from '../../config/app';
import { useApp } from '../../context/AppContext';
import ImageUpload from '../../components/common/ImageUpload';

const emptyForm = {
  id: null,
  imageUrl: '',
  route: '',
  isActive: false,
  startsAt: '',
  endsAt: '',
  displayDurationSeconds: 0,
  dismissible: true,
  showOnce: false
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusLabel = (popup) => {
  const now = Date.now();
  const start = Date.parse(popup.startsAt);
  const end = Date.parse(popup.endsAt);
  if (popup.isActive === false) return { label: 'Off', color: 'default' };
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { label: 'Invalid dates', color: 'error' };
  if (now < start) return { label: 'Scheduled', color: 'info' };
  if (now > end) return { label: 'Expired', color: 'warning' };
  return { label: 'Live', color: 'success' };
};

const AppWebsitePopupBanners = () => {
  const { actions } = useApp();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const res = await axios.get(`${appConfig.api.baseURL}/popup-banners`);
      if (res.data?.success) {
        setItems(res.data.data.popupBanners || []);
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to load popup banners', 'error');
      }
    } catch (error) {
      console.error('Error loading popup banners:', error);
      actions.showSnackbar('Failed to load popup banners', 'error');
    }
  }, [actions]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (popup) => {
    setForm({
      id: popup.id,
      imageUrl: popup.imageUrl || '',
      route: popup.route || '',
      isActive: popup.isActive === true,
      startsAt: toLocalInput(popup.startsAt),
      endsAt: toLocalInput(popup.endsAt),
      displayDurationSeconds: popup.displayDurationSeconds || 0,
      dismissible: popup.dismissible !== false,
      showOnce: popup.showOnce === true
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.imageUrl) {
      actions.showSnackbar('Upload an image first', 'warning');
      return;
    }
    if (!form.startsAt || !form.endsAt) {
      actions.showSnackbar('Start and end time are required', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        imageUrl: form.imageUrl,
        route: form.route.trim(),
        isActive: form.isActive,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        displayDurationSeconds: Number(form.displayDurationSeconds) || 0,
        dismissible: form.dismissible,
        showOnce: form.showOnce
      };

      const res = form.id
        ? await axios.put(`${appConfig.api.baseURL}/popup-banners/${form.id}`, payload)
        : await axios.post(`${appConfig.api.baseURL}/popup-banners`, payload);

      if (res.data?.success) {
        actions.showSnackbar(res.data.message || 'Popup banner saved', 'success');
        closeForm();
        loadItems();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to save popup banner', 'error');
      }
    } catch (error) {
      console.error('Error saving popup banner:', error);
      actions.showSnackbar(error.response?.data?.message || 'Failed to save popup banner', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${appConfig.api.baseURL}/popup-banners/${id}`);
      if (res.data?.success) {
        actions.showSnackbar('Popup banner deleted', 'success');
        if (form.id === id) closeForm();
        loadItems();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to delete popup banner', 'error');
      }
    } catch (error) {
      console.error('Error deleting popup banner:', error);
      actions.showSnackbar('Failed to delete popup banner', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            In-app popup banners
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shown as a centered modal on home. Only one popup can be active at a time; new popups start inactive.
          </Typography>
        </Box>
        {!showForm && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Create popup
          </Button>
        )}
      </Box>

      {showForm && (
        <Card sx={{ mb: 4 }}>
          <CardHeader
            title={form.id ? 'Edit popup' : 'Create popup'}
            action={
              <IconButton aria-label="Close form" onClick={closeForm}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent>
            <ImageUpload
              images={form.imageUrl ? [form.imageUrl] : []}
              onImagesChange={(urls) => setForm((prev) => ({ ...prev, imageUrl: urls[0] || '' }))}
              uploadPath="banners/popup"
              maxImages={1}
              multiple={false}
              label="Popup image"
              helperText="Use 1080×1350 px (4:5 portrait). Square 1080×1080 will look small on tall phones. Keep offer text inside the image; do not add Play/App Store buttons (user is already in the app)."
              showPreviews
            />
            <Stack spacing={2} mt={2}>
              <TextField
                label="On click route"
                value={form.route}
                onChange={(e) => setForm((prev) => ({ ...prev, route: e.target.value }))}
                placeholder="/products/chicken-curry-cut"
                fullWidth
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Starts at"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Ends at"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              <TextField
                label="On-screen seconds (0 = until user closes)"
                type="number"
                value={form.displayDurationSeconds}
                onChange={(e) => setForm((prev) => ({ ...prev, displayDurationSeconds: e.target.value }))}
                inputProps={{ min: 0, max: 60 }}
                fullWidth
              />
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {form.id && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                      />
                    }
                    label="Active (turning this on deactivates the others)"
                  />
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.dismissible}
                      onChange={(e) => setForm((prev) => ({ ...prev, dismissible: e.target.checked }))}
                    />
                  }
                  label="User can dismiss"
                />
                {form.id && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.showOnce}
                        onChange={(e) => setForm((prev) => ({ ...prev, showOnce: e.target.checked }))}
                      />
                    }
                    label="Show once per install"
                  />
                )}
              </Stack>
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.5 }}>
            <Button onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : form.id ? 'Update popup' : 'Create popup'}
            </Button>
          </CardActions>
        </Card>
      )}

      <Card>
        <CardHeader title="Saved popups" subheader="Landing returns the single active popup within its date window." />
        <CardContent>
          {items.length === 0 && (
            <Typography color="text.secondary">No popup banners yet.</Typography>
          )}
          <Stack spacing={2}>
            {items.map((popup) => {
              const status = statusLabel(popup);
              return (
                <Box
                  key={popup.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    alignItems: 'center'
                  }}
                >
                  <Box
                    component="img"
                    src={popup.imageUrl}
                    alt=""
                    sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Chip size="small" label={status.label} color={status.color} />
                      <Typography variant="body2">{popup.route || 'No route'}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {popup.startsAt} → {popup.endsAt}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => openEdit(popup)}>Edit</Button>
                  <IconButton aria-label="Delete popup" onClick={() => handleDelete(popup.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppWebsitePopupBanners;
